import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CheckCircle, Clock, MapPin, Package, Shield, Copy, Check, Loader2, Bike, Store, Navigation, XCircle, RefreshCw, AlertTriangle } from "lucide-react";
import axios from "axios";
import RatingModal from "../components/RatingModal";
import { getBackendUrl } from "../utils/api";

const API = `${getBackendUrl()}/api`;

// Status flow: placed → confirmed → preparing → ready → picked_up → on_the_way → delivered
// Also handles cancelled and auto_cancelled status
const STATUS_CONFIG = {
  placed: { icon: Clock, color: "text-yellow-500", bg: "bg-yellow-50", label: "Order sent to restaurant" },
  pending: { icon: Clock, color: "text-yellow-500", bg: "bg-yellow-50", label: "Order sent to restaurant" },
  payment_pending: { icon: Clock, color: "text-yellow-500", bg: "bg-yellow-50", label: "Waiting for payment..." },
  confirmed: { icon: Store, color: "text-blue-500", bg: "bg-blue-50", label: "Restaurant confirmed!" },
  preparing: { icon: Store, color: "text-orange-500", bg: "bg-orange-50", label: "Preparing your food..." },
  ready: { icon: Package, color: "text-purple-500", bg: "bg-purple-50", label: "Food ready! Finding rider..." },
  accepted: { icon: Bike, color: "text-blue-500", bg: "bg-blue-50", label: "Rider accepted!" },
  picked_up: { icon: Navigation, color: "text-purple-500", bg: "bg-purple-50", label: "On the way!" },
  reached_customer: { icon: MapPin, color: "text-green-500", bg: "bg-green-50", label: "Rider arrived!" },
  delivered: { icon: CheckCircle, color: "text-green-600", bg: "bg-green-50", label: "Delivered!" },
  cancelled: { icon: XCircle, color: "text-red-500", bg: "bg-red-50", label: "Order Cancelled" },
  auto_cancelled: { icon: XCircle, color: "text-red-500", bg: "bg-red-50", label: "Order Auto-Cancelled" }
};

// Refund status configuration
const REFUND_STATUS_CONFIG = {
  none: { icon: null, color: "", label: "" },
  initiated: { icon: RefreshCw, color: "text-blue-500", bg: "bg-blue-50", label: "Refund Initiated" },
  pending: { icon: RefreshCw, color: "text-yellow-500", bg: "bg-yellow-50", label: "Refund Processing" },
  success: { icon: CheckCircle, color: "text-green-500", bg: "bg-green-50", label: "Refund Completed" },
  failed: { icon: AlertTriangle, color: "text-red-500", bg: "bg-red-50", label: "Refund Failed" }
};

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [copied, setCopied] = useState(false);
  const [canDispute, setCanDispute] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const [hasShownRatingModal, setHasShownRatingModal] = useState(() => {
    // Check localStorage to see if we've already shown the modal for this order
    const dismissedRatings = JSON.parse(localStorage.getItem("fleety_dismissed_ratings") || "[]");
    return dismissedRatings.includes(orderId);
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  // Check if order has already been rated
  const checkRating = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/has-rating`);
      setHasRated(response.data.has_rating);
      
      // If already rated, mark as shown in localStorage too
      if (response.data.has_rating) {
        const dismissedRatings = JSON.parse(localStorage.getItem("fleety_dismissed_ratings") || "[]");
        if (!dismissedRatings.includes(orderId)) {
          dismissedRatings.push(orderId);
          localStorage.setItem("fleety_dismissed_ratings", JSON.stringify(dismissedRatings));
        }
      }
    } catch (e) {
      console.error("Error checking rating:", e);
    }
  }, [orderId]);

  // Poll for order status updates
  const trackOrder = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders/track/${orderId}`);
      setOrderData(response.data);
      setLoading(false);
      
      // Check dispute eligibility for delivered orders
      if (response.data.status === "delivered") {
        try {
          const disputeResponse = await axios.get(`${API}/orders/${orderId}/can-dispute`);
          setCanDispute(disputeResponse.data);
        } catch (e) {
          console.error("Error checking dispute eligibility:", e);
        }
        
        // Check if already rated, if not show rating modal
        checkRating();
      }
    } catch (error) {
      console.error("Error tracking order:", error);
      setLoading(false);
    }
  }, [orderId, checkRating]);

  // Show rating modal when order is delivered and not yet rated (only once ever per order)
  useEffect(() => {
    if (orderData?.status === "delivered" && !hasRated && !hasShownRatingModal) {
      // Small delay to let the user see the "Delivered!" status first
      const timer = setTimeout(() => {
        setShowRatingModal(true);
        setHasShownRatingModal(true);
        // Save to localStorage so it doesn't show again even after page refresh
        const dismissedRatings = JSON.parse(localStorage.getItem("fleety_dismissed_ratings") || "[]");
        if (!dismissedRatings.includes(orderId)) {
          dismissedRatings.push(orderId);
          localStorage.setItem("fleety_dismissed_ratings", JSON.stringify(dismissedRatings));
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [orderData?.status, hasRated, hasShownRatingModal, orderId]);

  useEffect(() => {
    if (user && orderId) {
      trackOrder();
      
      // Poll every 5 seconds for status updates
      const interval = setInterval(trackOrder, 5000);
      
      return () => clearInterval(interval);
    }
  }, [user, orderId, trackOrder]);

  const copyOtp = () => {
    if (orderData?.otp) {
      navigator.clipboard.writeText(orderData.otp);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const status = orderData?.status || "placed";
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.placed;
  const StatusIcon = statusConfig.icon;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3FA9F5] border-t-transparent"></div>
      </div>
    );
  }

  // Special handling for payment_pending status - show payment not completed screen
  if (status === "payment_pending") {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="order-success-page">
        <div className="h-6 bg-[#3FA9F5]"></div>
        <header className="bg-[#3FA9F5] px-4 py-6 text-center">
          <h1 className="text-2xl font-bold text-white">Fleety</h1>
        </header>
        
        <div className="max-w-md mx-auto px-4 py-8">
          {/* Payment Pending Card */}
          <div className="bg-yellow-50 rounded-xl p-6 mb-6 border-2 border-yellow-200">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 bg-white shadow-lg">
                <AlertTriangle size={40} className="text-yellow-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Not Completed</h2>
              <p className="text-sm text-yellow-700 mb-4">
                Your payment was not completed. The order has not been placed.
              </p>
              <p className="text-xs text-gray-500 mb-6">
                Order #{orderData?.order_number}
              </p>
            </div>
          </div>

          {/* What to do next */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">What happened?</h3>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-0.5">•</span>
                <span>Payment was initiated but not confirmed by the payment gateway</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-0.5">•</span>
                <span>No amount has been deducted from your account</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-0.5">•</span>
                <span>If money was deducted, it will be auto-refunded within 5-7 business days</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate("/home")}
              className="w-full h-12 bg-[#3FA9F5] hover:bg-[#2196F3] text-white font-semibold"
            >
              Try Ordering Again
            </Button>
            <Button
              onClick={() => navigate("/orders")}
              variant="outline"
              className="w-full h-12 border-gray-300 text-gray-700"
            >
              View All Orders
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="order-success-page">
      {/* Top Spacer for mobile */}
      <div className="h-6 bg-[#3FA9F5]"></div>
      
      {/* Header */}
      <header className="bg-[#3FA9F5] px-4 py-6 text-center">
        <h1 className="text-2xl font-bold text-white">Fleety</h1>
      </header>

      <div className="max-w-md mx-auto px-4 py-8">
        {/* Order Status Card */}
        <div className={`${statusConfig.bg} rounded-xl p-6 mb-6 border-2 border-opacity-50`}>
          <div className="text-center">
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 bg-white shadow-lg ${status === "delivered" || status === "cancelled" ? "" : "animate-pulse"}`}>
              <StatusIcon size={40} className={statusConfig.color} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-1" data-testid="success-title">
              {status === "delivered" ? "Order Delivered!" : (status === "cancelled" || status === "auto_cancelled") ? "Order Cancelled" : "Order Placed!"}
            </h2>
            <p className={`text-sm font-medium ${statusConfig.color}`}>
              {orderData?.status_message || "Processing your order..."}
            </p>
            {status !== "delivered" && status !== "cancelled" && status !== "auto_cancelled" && (
              <div className="flex items-center justify-center gap-2 mt-3 text-gray-400 text-xs">
                <Loader2 size={14} className="animate-spin" />
                <span>Auto-updating every 5 seconds</span>
              </div>
            )}
          </div>
        </div>

        {/* Cancellation Alert Card - Show when order is cancelled */}
        {orderData?.is_cancelled && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} className="text-red-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-800 mb-1">Order Cancelled</h3>
                <p className="text-sm text-red-600 mb-2">
                  {orderData.cancellation_reason || "Your order has been cancelled"}
                </p>
                {orderData.cancelled_at && (
                  <p className="text-xs text-red-400">
                    Cancelled at: {new Date(orderData.cancelled_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Dispute Status Card - Show when dispute exists */}
        {orderData?.dispute && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} className="text-purple-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-purple-800 mb-1">
                  Dispute: {orderData.dispute.issue_type_label}
                </h3>
                <p className="text-sm text-purple-600 mb-2">
                  Status: <span className="font-medium">{orderData.dispute.status_label}</span>
                </p>
                {orderData.dispute.status === "approved" && (
                  <p className="text-sm text-green-600 font-medium">
                    ✅ Your dispute has been approved. Refund is being processed.
                  </p>
                )}
                {orderData.dispute.status === "partial_refund" && (
                  <p className="text-sm text-blue-600 font-medium">
                    ✅ Partial refund approved.
                  </p>
                )}
                {orderData.dispute.status === "rejected" && (
                  <p className="text-sm text-red-600 font-medium">
                    ❌ Your dispute was not approved.
                  </p>
                )}
                {orderData.dispute.status === "open" && (
                  <p className="text-sm text-yellow-600 font-medium">
                    ⏳ Under review. We&apos;ll respond within 24-48 hours.
                  </p>
                )}
                
                {/* Show admin's resolution message */}
                {orderData.dispute.resolution && (
                  <div className="mt-3 bg-white rounded-lg p-3 border border-purple-100">
                    <p className="text-xs text-gray-500 mb-1 font-medium">Resolution from Fleety:</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{orderData.dispute.resolution}</p>
                  </div>
                )}
                
                {/* Show refund amount ONLY for approved/partial_refund disputes */}
                {(orderData.dispute.status === "approved" || orderData.dispute.status === "partial_refund") && orderData.dispute.refund_amount > 0 && (
                  <p className="text-sm text-green-600 font-medium mt-2">
                    💰 Refund: ₹{orderData.dispute.refund_amount}
                  </p>
                )}
                
                {orderData.dispute.resolved_at && (
                  <p className="text-xs text-gray-500 mt-2">
                    Resolved: {new Date(orderData.dispute.resolved_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Refund Status Card - Show when refund is initiated */}
        {orderData?.refund_status && orderData.refund_status !== "none" && (
          <div className={`${REFUND_STATUS_CONFIG[orderData.refund_status]?.bg || "bg-gray-50"} border rounded-xl p-4 mb-6`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-white`}>
                {orderData.refund_status === "pending" || orderData.refund_status === "initiated" ? (
                  <RefreshCw size={20} className={REFUND_STATUS_CONFIG[orderData.refund_status]?.color || "text-gray-500"} />
                ) : orderData.refund_status === "success" ? (
                  <CheckCircle size={20} className="text-green-500" />
                ) : (
                  <AlertTriangle size={20} className="text-red-500" />
                )}
              </div>
              <div className="flex-1">
                <h3 className={`font-semibold mb-1 ${
                  orderData.refund_status === "success" ? "text-green-800" : 
                  orderData.refund_status === "failed" ? "text-red-800" : "text-blue-800"
                }`}>
                  {REFUND_STATUS_CONFIG[orderData.refund_status]?.label || "Refund Status"}
                </h3>
                <p className={`text-sm mb-2 ${
                  orderData.refund_status === "success" ? "text-green-600" : 
                  orderData.refund_status === "failed" ? "text-red-600" : "text-blue-600"
                }`}>
                  {orderData.refund_message || "Processing your refund..."}
                </p>
                {orderData.refund_amount && (
                  <p className="text-sm font-medium text-gray-700">
                    Refund Amount: ₹{orderData.refund_amount}
                  </p>
                )}
                {orderData.estimated_refund_time && (
                  <p className="text-xs text-gray-500 mt-1">
                    ⏱️ Estimated time: {orderData.estimated_refund_time}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Status Progress - Hide for cancelled orders */}
        {status !== "cancelled" && status !== "auto_cancelled" && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Order Progress</h3>
          <div className="space-y-3">
            {[
              { key: "placed", label: "Order sent to restaurant" },
              { key: "confirmed", label: "Restaurant confirmed" },
              { key: "preparing", label: "Preparing your food" },
              { key: "ready", label: "Food ready, finding rider" },
              { key: "accepted", label: "Rider accepted" },
              { key: "picked_up", label: "On the way to you" },
              { key: "delivered", label: "Delivered!" }
            ].map((step, index) => {
              const config = STATUS_CONFIG[step.key] || STATUS_CONFIG.placed;
              const Icon = config.icon;
              const statusOrder = ["placed", "pending", "confirmed", "preparing", "ready", "accepted", "picked_up", "reached_customer", "delivered"];
              const currentIndex = statusOrder.indexOf(status);
              const stepIndex = statusOrder.indexOf(step.key);
              const isActive = step.key === status || (status === "pending" && step.key === "placed");
              const isPast = stepIndex <= currentIndex;
              
              return (
                <div key={step.key} className={`flex items-center gap-3 p-2 rounded-lg ${isActive ? config.bg : ""}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isPast ? "bg-green-100" : "bg-gray-100"}`}>
                    <Icon size={16} className={isPast ? "text-green-600" : "text-gray-400"} />
                  </div>
                  <span className={`text-sm ${isActive ? "font-semibold text-gray-900" : isPast ? "text-gray-700" : "text-gray-400"}`}>
                    {step.label}
                  </span>
                  {isActive && status !== "delivered" && <Loader2 size={14} className="animate-spin text-gray-400 ml-auto" />}
                  {isPast && !isActive && <CheckCircle size={14} className="text-green-500 ml-auto" />}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Delivery OTP Card - Show for all orders with OTP, but not for cancelled orders */}
        {orderData?.otp && status !== "delivered" && status !== "cancelled" && status !== "auto_cancelled" && (
          <div className="bg-gradient-to-r from-[#22C55E] to-[#16A34A] rounded-xl p-6 mb-6 text-white" data-testid="delivery-otp-card">
            <div className="flex items-center gap-3 mb-3">
              <Shield size={24} />
              <h3 className="font-semibold text-lg">🔐 Delivery OTP</h3>
            </div>
            <p className="text-white/80 text-sm mb-4">
              Share this OTP with the rider when receiving your food
            </p>
            <div className="flex items-center justify-between bg-white/20 rounded-lg p-4">
              <span className="text-4xl font-bold tracking-[0.3em]">{orderData.otp}</span>
              <button 
                onClick={copyOtp}
                className="p-3 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              >
                {copied ? <Check size={24} /> : <Copy size={24} />}
              </button>
            </div>
            <p className="text-white/70 text-xs mt-3">
              ⚠️ Without OTP, the rider cannot complete the delivery
            </p>
          </div>
        )}

        {/* Rider Info - Show when rider accepts */}
        {orderData?.rider_name && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Your Rider</h3>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Bike size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{orderData.rider_name}</p>
                {orderData.rider_phone && (
                  <p className="text-sm text-gray-500">{orderData.rider_phone}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Order Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6" data-testid="order-info">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Clock size={20} className="text-[#3FA9F5]" />
              <span className="text-gray-600">Estimated Delivery</span>
            </div>
            <span className="font-semibold text-gray-900">25-35 min</span>
          </div>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Package size={20} className="text-[#3FA9F5]" />
              <span className="text-gray-600">Order ID</span>
            </div>
            <span className="font-semibold text-gray-900 text-lg">#{orderData?.order_number || "N/A"}</span>
          </div>

          {orderData && (
            <>
              {/* Items Ordered */}
              <div className="border-t border-gray-100 pt-4 mt-4">
                <h3 className="font-semibold text-gray-900 mb-3">{orderData.restaurant_name}</h3>
                <div className="space-y-2">
                  {orderData.items?.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-gray-600">{item.name} × {item.quantity}</span>
                      <span>₹{item.price * item.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bill Breakdown */}
              <div className="border-t border-gray-100 pt-4 mt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Bill Details</h3>
                <div className="space-y-2 text-sm">
                  {/* Food Section */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Item Total</span>
                    <span>₹{Number(orderData.item_total ?? orderData.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Packing Charges</span>
                    <span>₹{Number(orderData.packing_charges ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-50 pt-1">
                    <span className="text-gray-700">Subtotal (Food + Packing)</span>
                    <span>₹{Number(orderData.subtotal ?? ((orderData.item_total ?? 0) + (orderData.packing_charges ?? 0))).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">GST on Food & Packing (5%)</span>
                    <span>₹{Number(orderData.food_gst ?? ((orderData.subtotal ?? 0) * 0.05)).toFixed(2)}</span>
                  </div>
                  
                  {/* Delivery & Platform Section */}
                  <div className="border-t border-gray-50 pt-1"></div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery Fee</span>
                    <span>₹{Number(orderData.delivery_fee ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Platform Fee</span>
                    <span>₹{Number(orderData.platform_fee ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">GST on Delivery & Platform (18%)</span>
                    <span>₹{Number(orderData.service_gst ?? (((orderData.delivery_fee ?? 0) + (orderData.platform_fee ?? 0)) * 0.18)).toFixed(2)}</span>
                  </div>
                  
                  {/* Total */}
                  <div className="flex justify-between font-bold text-base pt-2 border-t border-gray-100 mt-2">
                    <span>Total Paid</span>
                    <span className="text-[#3FA9F5]">₹{orderData.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="border-t border-gray-100 pt-4 mt-4">
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-[#3FA9F5] mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Delivering to</p>
                    <p className="text-sm font-medium text-gray-900">{orderData.delivery_address}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Dispute Button - Only for delivered orders within 24 hours */}
        {status === "delivered" && canDispute?.can_dispute && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-orange-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-800 mb-1">Having issues with your order?</h3>
                <p className="text-sm text-orange-600 mb-3">
                  You have {canDispute.hours_remaining?.toFixed(1)} hours left to raise a dispute
                </p>
                <Button
                  onClick={() => navigate(`/dispute/${orderId}`)}
                  variant="outline"
                  className="border-orange-400 text-orange-600 hover:bg-orange-100"
                >
                  Raise Issue
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            onClick={() => navigate("/home")}
            className="w-full h-14 bg-[#3FA9F5] hover:bg-[#2196F3] text-white text-lg font-semibold rounded-xl"
            data-testid="continue-ordering-btn"
          >
            Continue Ordering
          </Button>
          <Button
            onClick={() => navigate("/orders")}
            variant="outline"
            className="w-full h-14 border-2 border-[#3FA9F5] text-[#3FA9F5] hover:bg-[#3FA9F5] hover:text-white text-lg font-semibold rounded-xl"
            data-testid="view-orders-btn"
          >
            View All Orders
          </Button>
        </div>
      </div>

      {/* Rating Modal - Shows automatically when order is delivered */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        orderId={orderId}
        orderType="fleety"
        onRatingSubmitted={() => {
          setShowRatingModal(false);
          setHasRated(true);
        }}
      />
    </div>
  );
};

export default OrderSuccessPage;
